<template>
  <VueFinalModal
    v-model="showLightbox"
    content-class="mx-auto size-full transform-gpu bg-background md:max-w-[952px] lg:max-w-[1152px] xl:max-w-[1632px]"
    :z-index-base="9999999999"
    esc-to-close
  >
    <ImageLightbox v-model="showLightbox" v-model:current-slide="lightboxCurrentSlide" :images="lightboxImages" />
  </VueFinalModal>

  <section ref="emblaRef" class="overflow-hidden sm:overflow-visible">
    <div class="-ml-5 grid auto-cols-[100%] grid-flow-col sm:mx-0 sm:grid-flow-row sm:grid-cols-2 sm:gap-5" style="backface-visibility: hidden; touch-action: pan-y pinch-zoom">
      <template v-for="item of galleryImages" :key="item.index">
        <div v-if="item.type === 'hero'" class="pl-5 sm:col-span-2 sm:px-0">
          <div class="relative">
            <button
              class="block w-full overflow-hidden rounded-3xl"
              @click="openLightbox(item.dimensionsImage && isMeasurementsVisible ? item.dimensionsImage.index : item.heroImageAttributes.index)"
            >
              <OnImg
                class="w-full"
                :src="item.heroImageAttributes.url"
                :alt="item.heroImageAttributes.alt"
                :width="item.heroImageAttributes.width"
                :height="item.heroImageAttributes.height"
                sizes="sm:678px md:1024px lg:1280px"
              />
            </button>
            <template v-if="item.dimensionsImage">
              <button
                class="peer absolute bottom-6 left-1/2 z-10 hidden w-40 -translate-x-1/2 items-center justify-center rounded-full bg-background-medium px-4 py-2 text-button-m transition-opacity sm:flex"
                :class="{
                  'opacity-33': isMeasurementsVisible,
                  'opacity-100': !isMeasurementsVisible,
                  'hover:opacity-33': !measurementButtonHoverDisabled,
                }"
                @click="toggleMeasurements"
                @mouseenter="measurementButtonHover = true"
                @mouseleave="measurementButtonHover = false"
              >
                {{ t['w.module.image_viewer.measurements']() }}
                <Icon name="box" class="ml-0.5 size-5" />
              </button>
              <OnImg
                class="pointer-events-none absolute inset-0 z-0 w-full overflow-hidden rounded-3xl transition-opacity"
                :class="{
                  'opacity-100': isMeasurementsVisible,
                  'opacity-0': !isMeasurementsVisible,
                  'peer-hover:opacity-100': !measurementButtonHoverDisabled,
                }"
                :src="item.dimensionsImage.url"
                :alt="item.dimensionsImage.alt"
                :width="item.dimensionsImage.width"
                :height="item.dimensionsImage.height"
                sizes="sm:678px md:1024px lg:1280px"
              />
            </template>
          </div>
        </div>

        <div v-else-if="item.type === 'image'" class="pl-5 sm:px-0" :class="{ 'sm:hidden': !areAllImagesVisible && !item.aboveFold }">
          <button class="block w-full overflow-hidden rounded-3xl" @click="openLightbox(item.index)">
            <OnImg class="w-full" :src="item.url" :alt="item.alt" :width="item.width" :height="item.height" sizes="sm:337px md:512px lg:640px" />
          </button>
        </div>
      </template>
    </div>
  </section>

  <template v-if="galleryImages.length > 1">
    <ol v-if="galleryImages.length <= 12" class="mt-3 flex justify-center sm:hidden">
      <li v-for="(_, index) in galleryImages" :key="index">
        <button
          class="relative block size-6 before:absolute before:inset-2 before:size-2 before:rounded-full before:bg-current before:transition-colors hover:text-border-active"
          :class="{
            'text-border': selectedIndex === index,
            'text-border-medium': selectedIndex !== index,
          }"
          type="button"
          @click="() => emblaApi?.scrollTo(index)"
        >
          <span class="sr-only">{{ t['w.global.goto_slide']() }} {{ index + 1 }}</span>
        </button>
      </li>
    </ol>
    <div v-else class="mt-3 text-center text-sm sm:hidden">{{ selectedIndex + 1 }} / {{ galleryImages.length }}</div>
  </template>

  <Button
    v-if="gallery.find((item) => item.type === 'hero' && item.dimensionsImage)"
    class="btn-rounded mx-auto mt-3 block w-40 sm:hidden"
    :class="isMeasurementsVisible ? 'btn-black' : 'btn-medium'"
    icon-name="box"
    @click="onSliderButtonPress"
  >
    {{ t['w.module.image_viewer.measurements']() }}
  </Button>

  <!-- size-0 works around video disappearing when entering fullscreen and responsive breakpoint changing as a result, absolute promotes the element to a different stacking context -->
  <EmbeddedYoutube
    v-for="item of gallery.filter((item) => item.type === 'video')"
    :key="item.index"
    :class="{ 'sm:absolute sm:size-0': !areAllImagesVisible && !item.aboveFold }"
    :video-id="item.youtubeId"
    class="my-5 w-full overflow-hidden rounded-3xl"
  />

  <Button
    v-if="gallery.some((image) => image.aboveFold === false)"
    class="btn-outline btn-rounded mx-auto mt-5 hidden sm:block"
    @click="areAllImagesVisible = !areAllImagesVisible"
  >
    {{ areAllImagesVisible ? t['w.module.image_viewer.show_less_images']() : t['w.module.image_viewer.show_all_images']() }}
  </Button>
</template>

<script setup lang="ts">
import { VueFinalModal } from 'vue-final-modal'
import { useResizeObserver } from '@vueuse/core'

import ImageLightbox from './ImageLightbox.vue'
import emblaCarouselVue from 'embla-carousel-vue'

import * as t from '~/messages'

import EmbeddedYoutube from '~/components/product/EmbeddedYoutube.vue'
import Button from '~/components/button/Button.vue'
import OnImg from '~/components/on-img/OnImg.vue'

const [emblaRef, emblaApi] = emblaCarouselVue({
  align: 'start',
  slidesToScroll: 1,
  active: true,
  breakpoints: {
    '(min-width: 680px)': {
      active: false,
    },
  },
})

interface Image {
  dimensionsThumbnailUri: string | null
  aUrl: string
  bUrl: string
  cUrl: string
  cAlt: string
  cWidth: number
  cHeight: number
  dUrl: string | null
  dAlt: string | null
  dWidth: number | null
  dHeight: number | null
}

const props = withDefaults(
  defineProps<{
    images?: Image[]
    youtubeId?: string | null
  }>(),
  {
    images: () => [],
    youtubeId: null,
  }
)

const isMeasurementsVisible = ref<boolean>(false)
const measurementButtonHoverDisabled = ref<boolean>(false)
const measurementButtonHover = ref(false)

const toggleMeasurements = () => {
  isMeasurementsVisible.value = !isMeasurementsVisible.value
  measurementButtonHoverDisabled.value = true
}

watch(measurementButtonHover, (newValue) => {
  if (!newValue) {
    measurementButtonHoverDisabled.value = false
  }
})

const areAllImagesVisible = ref<boolean>(false)

const showLightbox = ref<boolean>(false)
const lightboxCurrentSlide = ref(0)

const openLightbox = (index: number) => {
  lightboxCurrentSlide.value = index
  showLightbox.value = true
}

const onSliderButtonPress = () => {
  if (emblaApi.value) {
    const heroImageIndex = gallery.value.findIndex((item) => item.type === 'hero')

    if (heroImageIndex === -1) {
      return
    }

    emblaApi.value.scrollTo(heroImageIndex)
    toggleMeasurements()
  }
}

const isScrollable = ref(true)

const toggleActiveWhenScrollable = () => {
  isScrollable.value = (emblaApi.value?.internalEngine().scrollSnaps.length ?? 0) > 1
}

watch(isScrollable, () => {
  emblaApi.value?.reInit({ active: isScrollable.value })
})

const selectedIndex = ref(0)

onMounted(() => {
  const updateSelectedIndex = () => (selectedIndex.value = emblaApi.value?.selectedScrollSnap() ?? 0)

  if (emblaApi.value) {
    emblaApi.value.on('reInit', updateSelectedIndex).on('select', updateSelectedIndex)

    toggleActiveWhenScrollable()
  }
})

useResizeObserver(
  emblaRef,
  useThrottleFrame(() => {
    toggleActiveWhenScrollable()
  })
)

const heroImageComponents = computed(() => {
  const dimensionsImage = props.images.findIndex((image) => image.dimensionsThumbnailUri !== null)
  const regularImage = props.images.findIndex((image) => image.dimensionsThumbnailUri === null)

  return [dimensionsImage === -1 ? null : dimensionsImage, regularImage === -1 ? null : regularImage]
})

const getHighestQualityImageAttributes = (image: Image) => {
  const url = image.dUrl === null ? image.cUrl : image.dUrl
  const alt = image.dAlt === null ? image.cAlt : image.dAlt
  const width = image.dWidth === null ? image.cWidth : image.dWidth
  const height = image.dHeight === null ? image.cHeight : image.dHeight
  const isDimensionsImage = image.dimensionsThumbnailUri !== null

  return {
    url,
    alt,
    width,
    height,
    isDimensionsImage,
  }
}

const gallery = computed(() => {
  const result = []
  let index = 0

  let aboveFoldCounter = 4

  if (heroImageComponents.value) {
    const dimensionsImageIndex = heroImageComponents.value[0]
    const heroImageIndex = heroImageComponents.value[1]

    const dimensionsImageAttributes = dimensionsImageIndex === null ? null : getHighestQualityImageAttributes(props.images[dimensionsImageIndex])
    const heroImageAttributes = heroImageIndex === null ? null : getHighestQualityImageAttributes(props.images[heroImageIndex])

    if (heroImageAttributes) {
      result.push({
        type: 'hero',
        dimensionsImage: dimensionsImageAttributes
          ? {
              ...dimensionsImageAttributes,
              index: dimensionsImageIndex,
            }
          : null,
        heroImageAttributes: {
          ...heroImageAttributes,
          index: heroImageIndex,
        },
      })
    } else if (dimensionsImageAttributes) {
      result.push({
        type: 'hero',
        dimensionsImage: null,
        heroImageAttributes: {
          ...dimensionsImageAttributes,
          index: dimensionsImageIndex,
        },
      })
    }
  }

  props.images.forEach((image) => {
    if ((heroImageComponents.value ?? []).includes(index)) {
      index++
      return
    }

    const { url, alt, width, height } = getHighestQualityImageAttributes(image)

    result.push({
      type: 'image',
      url,
      alt,
      width,
      height,
      index: index++,
      aboveFold: aboveFoldCounter-- > 0,
    })
  })

  if (props.youtubeId) {
    result.push({
      type: 'video',
      youtubeId: props.youtubeId,
      index: index++,
      aboveFold: aboveFoldCounter-- > 0,
    })
  }

  return result
})

const galleryImages = computed(() => gallery.value.filter((image) => ['hero', 'image'].includes(image.type)))

const lightboxImages = computed(() =>
  props.images.map((image, index) => {
    const { url, alt, width, height, isDimensionsImage } = getHighestQualityImageAttributes(image)

    return {
      url,
      alt,
      width,
      height,
      isDimensionsImage,
      index,
    }
  })
)
</script>
